import React from 'react';
import s from './Select.module.css';
import { ReactComponent as ArrowIcon } from '../../icons/select_arrow.svg';

export function Select({
  value,
  onChange,
  ariaLabel,
  selectRef,
  className = '',
  children,
  disabled = false,
  ...rest
}) {
  const innerRef = React.useRef(null);
  const ref = selectRef ?? innerRef;
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef(null);

  const options = React.useMemo(() => {
    return React.Children.toArray(children)
      .filter(React.isValidElement)
      .map((child) => ({
        value: child.props.value,
        label: child.props.children,
        disabled: child.props.disabled,
      }));
  }, [children]);

  const selectedIndex = React.useMemo(
    () => options.findIndex((o) => String(o.value) === String(value)),
    [options, value]
  );
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;
  const [activeIndex, setActiveIndex] = React.useState(
    selectedIndex >= 0 ? selectedIndex : 0
  );

  React.useEffect(() => {
    if (selectedIndex >= 0) setActiveIndex(selectedIndex);
  }, [selectedIndex]);

  React.useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      const root = containerRef.current;
      if (root && !root.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const emitChange = (newValue) => {
    if (typeof onChange === 'function') {
      onChange({ target: { value: newValue } });
    }
  };

  const moveActive = (direction) => {
    if (!options.length) return;
    let idx = activeIndex;
    for (let i = 0; i < options.length; i += 1) {
      idx = (idx + direction + options.length) % options.length;
      if (!options[idx].disabled) break;
    }
    setActiveIndex(idx);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else {
        moveActive(1);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else {
        moveActive(-1);
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else if (options[activeIndex] && !options[activeIndex].disabled) {
        emitChange(options[activeIndex].value);
        setOpen(false);
      }
    }
  };

  const handleBlur = (e) => {
    if (typeof rest.onBlur === 'function') rest.onBlur(e);
  };

  const boxClass = `${s.box} ${open ? s.boxOpen : ''} ${className}`.trim();

  return (
    <div ref={containerRef} className={boxClass}>
      <button
        type="button"
        className={`${s.display} ${disabled ? s.displayDisabled : ''}`}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={s.displayText}>
          {selectedOption ? selectedOption.label : ''}
        </span>
      </button>
      {open && options.length > 0 && (
        <ul role="listbox" className={s.menu}>
          {options.map((opt, idx) => {
            const isSelected =
              String(opt.value) === String(value);
            const isActive = idx === activeIndex;
            const cls = [
              s.option,
              isSelected ? s.optionSelected : '',
              isActive ? s.optionActive : '',
              opt.disabled ? s.optionDisabled : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <li
                key={opt.value ?? idx}
                role="option"
                aria-selected={isSelected}
                className={cls}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (opt.disabled) return;
                  emitChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
      <select
        ref={ref}
        className={s.nativeSelect}
        value={value}
        onChange={onChange}
        aria-hidden="true"
        tabIndex={-1}
        disabled={disabled}
        {...rest}
      >
        {children}
      </select>
      <span className={s.icon} aria-hidden="true">
        <ArrowIcon />
      </span>
    </div>
  );
}
